import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const CurrencyContext = createContext();

const CURRENCY_SYMBOLS = {
  GBP: '£',
  EUR: '€',
  USD: '$'
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    // Get from localStorage or default to GBP
    return localStorage.getItem('mirevy_currency') || 'GBP';
  });
  const [rates, setRates] = useState({ GBP: 1, EUR: 1.18, USD: 1.27 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const data = await api.getExchangeRates();
        setRates(data.rates);
      } catch (error) {
        console.error('Failed to fetch exchange rates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
    // Refresh rates every hour
    const interval = setInterval(fetchRates, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const changeCurrency = (newCurrency) => {
    if (['GBP', 'EUR', 'USD'].includes(newCurrency)) {
      setCurrency(newCurrency);
      localStorage.setItem('mirevy_currency', newCurrency);
    }
  };

  const convertPrice = (priceInGBP) => {
    if (currency === 'GBP') return priceInGBP;
    return priceInGBP * (rates[currency] || 1);
  };

  const formatPrice = (priceInGBP, showCurrency = true) => {
    const converted = convertPrice(priceInGBP);
    const symbol = CURRENCY_SYMBOLS[currency];
    const formatted = converted.toFixed(2);
    
    if (showCurrency) {
      return `${symbol}${formatted}`;
    }
    return formatted;
  };

  const getSymbol = () => CURRENCY_SYMBOLS[currency];

  return (
    <CurrencyContext.Provider value={{
      currency,
      rates,
      loading,
      changeCurrency,
      convertPrice,
      formatPrice,
      getSymbol,
      currencies: ['GBP', 'EUR', 'USD']
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
